import styled from '@emotion/styled';
import AnimationWrapper from 'components/AnimationWrapper';
import StyledLink from 'components/StyledLink';
import Image from 'components/Image';
import TypeReveal from 'components/TypeReveal';
import PropTypes from 'prop-types';
import { RiArrowUpLine, RiCopyrightLine } from 'react-icons/ri';
import useScrollPosition from 'hooks/useScrollPosition';
import useScrollTop from 'hooks/useScrollTop';

const Container = styled.footer`
  position: relative;
  z-index: 1;
  overflow: hidden;
`;
const Content = styled.div`
  width: 100%;
  margin: 0 auto;
  ${({ theme }) => theme.mixins.flexColCenter};
  gap: 12rem;
  padding: 12rem 2rem;
  padding-bottom: 0rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 12rem 3rem;
    padding-bottom: 0rem;
  }
`;
const Headline = styled.h2`
  cursor: default;
  ${({ theme }) => theme.mixins.HeadlineXLMobile};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 0rem;
  width: 100%;
  min-height: 11rem;
  text-align-last: justify;

  span:first-of-type {
    color: ${({ theme }) => theme.colors.accent};
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    ${({ theme }) => theme.mixins.HeadlineXL};
    min-height: 22.4rem;
    gap: 3rem;
    text-align-last: auto;

    span:last-of-type {
      width: max-content;
      align-self: flex-end;
    }
  }
`;
const FooterArea = styled.div`
  width: 100%;
  padding: 3rem 0rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 3rem;
  ${({ theme }) => theme.mixins.MonoM};
  color: ${({ theme }) => theme.colors.grayLight};

  button {
    order: 2;
  }

  > span {
    order: 1;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    button {
      order: 0;
    }

    > span {
      order: 0;
    }
  }
`;
const Copyright = styled.span`
  ${({ theme }) => theme.mixins.flexRowCenter};
  gap: 1rem;
  cursor: default;

  svg {
    font-size: 1em;
  }
`;
const Socials = styled.div`
  width: 100%;

  section {
    display: flex;
    justify-content: center;
    gap: clamp(1rem, 2vw, 3rem);
    flex-wrap: wrap;
  }

  section span {
    margin: 0 auto;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    width: max-content;
  }
`;
const ToTop = styled.button`
  all: unset;
  visibility: ${({ scrolled }) => (scrolled ? 'visible' : 'hidden')};
  ${({ theme }) => theme.mixins.flexRowCenter};
  gap: 0.8rem;
  cursor: pointer;
  transition: color 0.3s ease;

  svg {
    font-size: 1.2em;
    transition: transform 0.2s ease;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.white};

      svg {
        transform: translateY(-0.4rem);
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.lg} {
  }
`;
const ImageOverlay = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  z-index: -2;
  background-color: ${({ theme }) => theme.colors.black};
  opacity: 0.25;
`;
const BackgroundImage = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  z-index: -3;

  img {
    object-position: 50% 20%;
  }
`;
const SocialsFooter = ({ content }) => {
  const { headline1, headline2, socials, backgroundImage } = content;
  const currentYear = new Date().getFullYear();
  const scrolled = useScrollPosition(0);
  return (
    <Container>
      <Content>
        <Headline>
          <TypeReveal
            text={headline1}
            threshold={0.4}
            speed={80}
            startDelay={400}
            retriggerOnScroll={true}
          />
          <TypeReveal
            text={headline2}
            threshold={0.4}
            speed={80}
            startDelay={1400}
            retriggerOnScroll={true}
          />
        </Headline>
        <FooterArea>
          <Copyright>
            <RiCopyrightLine aria-hidden="true" />
            <p>{currentYear} Tyron Shi</p>
          </Copyright>
          {socials && (
            <Socials>
              <AnimationWrapper stagger={0.1} threshold={0.4} as="section">
                {socials.map((social, index) => (
                  <span key={index}>
                    <StyledLink label={social.label} url={social.url} />
                  </span>
                ))}
              </AnimationWrapper>
            </Socials>
          )}
          <ToTop onClick={useScrollTop} scrolled={scrolled}>
            To Top
            <RiArrowUpLine aria-hidden="true" />
          </ToTop>
        </FooterArea>
      </Content>
      <ImageOverlay></ImageOverlay>
      <BackgroundImage>
        <Image
          src={backgroundImage}
          alt="footer hero image"
          widths={[860, 1080, 1440, 2160]}
        />
      </BackgroundImage>
    </Container>
  );
};
SocialsFooter.propTypes = {
  content: PropTypes.shape({
    headline1: PropTypes.string.isRequired,
    headline2: PropTypes.string.isRequired,
    socials: PropTypes.array.isRequired,
    backgroundImage: PropTypes.string.isRequired,
  }),
};
export default SocialsFooter;
